import { useIsMobile } from "@roman/shared/hooks/soccer/useIsMobile";
import type { PredictionMarketSnapshot } from "@roman/shared/types/PredictionMarket";
import { useState } from "react";
import { PredictionChart } from ".";
import { CorrectCircleIcon } from "../CorrectCircleIcon";
import { IncorrectCircleIcon } from "../IncorrectCircleIcon";
import { createChartConfig } from "./utils/chartConfig";
import type { Choice } from "./utils/choiceSelection";

interface Props {
  choices: Choice[];
  predictionMarketSnapshots: PredictionMarketSnapshot[];
  winningChoiceId: string;
  realEndTime?: number | null;
}

const ChartBar = ({
  choice,
  color = "#000",
  isWinning,
  onClick,
  isOpen,
}: { choice: Choice; color?: string; isWinning?: boolean; onClick: () => void; isOpen: boolean }) => {
  return (
    <div
      className="grid grid-cols-[1fr_auto] items-center rounded-md px-4 py-2.5 bg-opacity-10n cursor-pointer"
      style={{ backgroundColor: `${color}1A` }}
      onClick={onClick}
    >
      <div className="flex items-center">
        {isWinning ? (
          <CorrectCircleIcon className="w-5 h-5 mr-4" style={{ color: color }} />
        ) : (
          <IncorrectCircleIcon className="w-5 h-5 mr-4" style={{ color: color }} />
        )}
        <span className="text-lg mr-2">{choice.name}</span>
        {choice.latestProbability && (
          <span className="text-lg font-bold">{Math.round(choice.latestProbability * 100)}%</span>
        )}
      </div>
      <div className={`transition-transform ${isOpen ? "rotate-180" : ""}`}>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.292893 1.12883C-0.0675907 1.48931 -0.0953202 2.05655 0.209705 2.44884L0.292893 2.54304L5.29289 7.54304C5.65338 7.90353 6.22061 7.93126 6.6129 7.62623L6.70711 7.54304L11.7071 2.54304C12.0976 2.15252 12.0976 1.51936 11.7071 1.12883C11.3466 0.768347 10.7794 0.740617 10.3871 1.04564L10.2929 1.12883L6 5.42094L1.70711 1.12883C1.34662 0.768347 0.779392 0.740617 0.387101 1.04564L0.292893 1.12883Z"
            fill={color}
          />
        </svg>
      </div>
    </div>
  );
};

export const ChartList = ({ choices, predictionMarketSnapshots, winningChoiceId, realEndTime }: Props) => {
  const chartConfig = createChartConfig(choices);
  const isMobile = useIsMobile();
  const [openChartIds, setOpenChartIds] = useState(() => {
    return new Set();
  });

  const toggleChart = (choiceId: string) => {
    setOpenChartIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(choiceId)) {
        newSet.delete(choiceId);
      } else {
        newSet.clear();
        newSet.add(choiceId);
      }
      return newSet;
    });
  };

  return (
    <div className="grid gap-2">
      {choices.map((choice) => {
        const isChartOpen = openChartIds.has(choice.id);

        return (
          <div className="w-full overflow-hidden" key={choice.id}>
            <ChartBar
              choice={choice}
              color={chartConfig[choice.id]?.color}
              isWinning={choice.id === winningChoiceId}
              onClick={() => {
                toggleChart(choice.id);
              }}
              isOpen={isChartOpen}
            />
            {isChartOpen && (
              <div>
                <div className="flex flex-col items-end mt-2">
                  <img
                    src="/assets/branch_text.png"
                    alt="Branch text"
                    className={isMobile ? "w-[85px] h-[33px]" : "h-[40px] w-[100px]"}
                  />
                </div>
                <PredictionChart
                  predictionMarketSnapshots={predictionMarketSnapshots}
                  defaultShowChartsIds={[choice.id]}
                  realEndTime={realEndTime}
                  isLineVisible={isChartOpen}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
